import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ProductGridRowDto, ProductStatus } from '@ecom/contracts';
import { AsyncSelect } from '@/components/async-select';
import { FilterBar, FilterField } from '@/components/filters';
import { BoxIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, Table, TableScroll, Td, Th, Thumb } from '@/components/page';
import { Alert, Button, ButtonLink, EmptyState, Select, TableSkeleton, cx } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { categoryOptions } from '@/lib/options';
import { halalasToRiyals } from '@/lib/price-input';
import {
  cellValid,
  changesOf,
  productCell,
  variantCell,
  type ProductCell,
  type VariantCell,
} from '@/lib/product-grid';
import { useProductGridStore } from '@/stores/product-grid.store';
import { useAuthStore } from '@/stores/auth.store';
import { can } from '@/lib/session';

const STATUSES: ProductStatus[] = ['DRAFT', 'ACTIVE', 'ARCHIVED'];

/**
 * The product grid (ADR-090): many products edited like a spreadsheet — one
 * row per variant, the product's own cells on its first row. Changed cells are
 * marked; Save sends only them, and a row the API refuses keeps what was typed
 * beside its reason while the rest are saved.
 */
export function ProductGridPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const user = useAuthStore((state) => state.user);
  const store = useProductGridStore();
  const { rows, nextCursor, total, search, categoryId, status, loading, error, saving, saveError, lastSaved, refused, load, loadMore, save, discard } = store;
  const [categoryLabel, setCategoryLabel] = useState('');
  // A stock figure is a recount (ADR-089): offered to whoever may recount.
  const mayRecount = can(user, 'inventory:manage');

  useEffect(() => {
    void load();
  }, [load]);

  const changes = changesOf(store);
  const pending = changes ? (changes.products?.length ?? 0) + (changes.variants?.length ?? 0) : null;
  const edited = Object.keys(store.productEdits).length + Object.keys(store.variantEdits).length;

  const actions = (
    <div className="flex items-center gap-2">
      {edited > 0 ? (
        <Button variant="secondary" disabled={saving} onClick={discard}>
          {t('gridDiscard')}
        </Button>
      ) : null}
      <Button disabled={saving || !pending} onClick={() => void save()}>
        {saving ? t('saving') : pending ? t('gridSaveCount', { count: count(pending, locale) }) : t('save')}
      </Button>
    </div>
  );

  return (
    <Page title={t('productGrid')} subtitle={t('productGridSubtitle')} back={{ to: '/products', label: t('products') }} actions={actions} fill>
      {lastSaved && Object.keys(refused).length === 0 ? (
        <Alert tone="success">{t('gridSaved', { products: count(lastSaved.products, locale), variants: count(lastSaved.variants, locale) })}</Alert>
      ) : null}
      {Object.keys(refused).length > 0 ? <Alert>{t('gridRefused', { count: count(Object.keys(refused).length, locale) })}</Alert> : null}
      {pending === null ? <Alert>{t('gridInvalid')}</Alert> : null}
      {saveError ? <Alert>{saveError}</Alert> : null}

      <ListCard>
        <ListToolbar>
          <FilterBar
            search={search}
            onSearch={(next) => void load({ search: next })}
            placeholder={t('searchProducts')}
            total={total ?? undefined}
            shown={new Set(rows.map((row) => row.productId)).size}
            chips={[
              ...(categoryId
                ? [{ key: 'category', label: categoryLabel || t('category'), onRemove: () => void load({ categoryId: '' }) }]
                : []),
              ...(status ? [{ key: 'status', label: t(`status${status}`), onRemove: () => void load({ status: '' }) }] : []),
            ]}
            onClearAll={() => void load({ search: '', categoryId: '', status: '' })}
          >
            <FilterField label={t('category')} htmlFor="grid-category">
              <AsyncSelect
                id="grid-category"
                value={categoryId}
                emptyLabel={t('allCategories')}
                fetchPage={categoryOptions.page}
                fetchOne={categoryOptions.one}
                onChange={(id, option) => {
                  setCategoryLabel(option?.label ?? '');
                  void load({ categoryId: id });
                }}
              />
            </FilterField>
            <FilterField label={t('productStatus')} htmlFor="grid-status">
              <Select id="grid-status" className="w-full" value={status} onChange={(event) => void load({ status: event.target.value as ProductStatus | '' })}>
                <option value="">{t('anyValue')}</option>
                {STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {t(`status${option}`)}
                  </option>
                ))}
              </Select>
            </FilterField>
          </FilterBar>
          <p className="text-xs text-ink-muted">{t('productGridHint')}</p>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && rows.length === 0 ? (
          <TableSkeleton columns={8} />
        ) : rows.length === 0 ? (
          <EmptyState title={t('noProducts')} icon={<BoxIcon />} action={<ButtonLink to="/products">{t('products')}</ButtonLink>} />
        ) : (
          <TableScroll className={cx('transition-opacity', loading && 'opacity-60')}>
            <Table>
              <thead>
                <tr>
                  <Th>{t('product')}</Th>
                  <Th>{t('productStatus')}</Th>
                  <Th>{t('gridBasePrice')}</Th>
                  <Th>{t('sku')}</Th>
                  <Th>{t('gridVariantPrice')}</Th>
                  <Th>{t('gridCostPrice')}</Th>
                  <Th>{t('stock')}</Th>
                  <Th>{t('barcode')}</Th>
                  <Th>{t('gridMpn')}</Th>
                  <Th>{t('gridWeight')}</Th>
                </tr>
              </thead>
              <tbody aria-busy={loading}>
                {rows.map((row) => (
                  <GridRow key={row.variantId} row={row} mayRecount={mayRecount} />
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
        <LoadMore visible={Boolean(nextCursor)} loading={loading} onClick={() => void loadMore()} />
      </ListCard>
    </Page>
  );
}

function GridRow({ row, mayRecount }: { row: ProductGridRowDto; mayRecount: boolean }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { productEdits, variantEdits, refused, editProduct, editVariant } = useProductGridStore();
  const productReason = refused[`p:${row.productId}`];
  const variantReason = refused[`v:${row.variantId}`];
  const options = Object.values(row.options).join(' · ');

  const product = (cell: ProductCell) => productEdits[row.productId]?.[cell] ?? productCell(row, cell);
  const variant = (cell: VariantCell) => variantEdits[row.variantId]?.[cell] ?? variantCell(row, cell);
  const productChanged = (cell: ProductCell) => product(cell) !== productCell(row, cell);
  const variantChanged = (cell: VariantCell) => variant(cell) !== variantCell(row, cell);

  const cellInput = (
    value: string,
    changed: boolean,
    valid: boolean,
    onChange: (value: string) => void,
    props: { label: string; numeric?: boolean; width?: string; placeholder?: string; rtl?: boolean },
  ) => (
    <input
      aria-label={props.label}
      dir={props.rtl ? 'rtl' : props.numeric ? 'ltr' : undefined}
      inputMode={props.numeric ? 'decimal' : undefined}
      value={value}
      placeholder={props.placeholder}
      onChange={(event) => onChange(event.target.value)}
      aria-invalid={!valid || undefined}
      className={cx(
        'h-8 rounded-control border bg-surface px-2 text-sm text-ink placeholder:text-ink-faint focus:border-brand-500',
        props.width ?? 'w-24',
        !valid ? 'border-danger' : changed ? 'border-brand-500 bg-brand-50' : 'border-line',
      )}
    />
  );

  return (
    <tr className={cx('align-top', row.firstOfProduct ? 'border-t-2 border-line' : 'border-t border-line', (productReason || variantReason) && 'bg-danger/5')}>
      <Td>
        {row.firstOfProduct ? (
          <div className="flex min-w-72 gap-3">
            <Thumb src={mediaUrl(row.imageUrl)} size="sm" />
            <div className="flex flex-col gap-1">
              {cellInput(product('nameAr'), productChanged('nameAr'), true, (value) => editProduct(row, 'nameAr', value), {
                label: `${t('nameArabic')} — ${row.sku}`,
                width: 'w-56',
                rtl: true,
              })}
              {cellInput(product('nameEn'), productChanged('nameEn'), cellValid('nameEn', product('nameEn')), (value) => editProduct(row, 'nameEn', value), {
                label: `${t('nameEnglish')} — ${row.sku}`,
                width: 'w-56',
              })}
              <Link to={`/products/${row.productId}`} className="text-xs text-brand-700 hover:underline">
                {t('gridOpenEditor')}
              </Link>
              {productReason ? <span className="text-xs text-danger">{productReason}</span> : null}
            </div>
          </div>
        ) : (
          <span className="block ps-12 text-xs text-ink-muted">{options || '—'}</span>
        )}
      </Td>
      <Td>
        {row.firstOfProduct ? (
          <select
            aria-label={`${t('productStatus')} — ${row.sku}`}
            value={product('status')}
            onChange={(event) => editProduct(row, 'status', event.target.value)}
            className={cx('h-8 rounded-control border bg-surface ps-2 pe-7 text-sm', productChanged('status') ? 'border-brand-500 bg-brand-50' : 'border-line')}
          >
            {STATUSES.map((option) => (
              <option key={option} value={option}>
                {t(`status${option}`)}
              </option>
            ))}
          </select>
        ) : null}
      </Td>
      <Td>
        {row.firstOfProduct
          ? cellInput(product('basePrice'), productChanged('basePrice'), cellValid('basePrice', product('basePrice')), (value) => editProduct(row, 'basePrice', value), {
              label: `${t('gridBasePrice')} — ${row.sku}`,
              numeric: true,
            })
          : null}
      </Td>
      <Td>
        {cellInput(variant('sku'), variantChanged('sku'), cellValid('sku', variant('sku')), (value) => editVariant(row, 'sku', value), {
          label: `${t('sku')} — ${row.sku}`,
          numeric: true,
          width: 'w-36',
        })}
        {variantReason ? <span className="mt-1 block max-w-48 text-xs text-danger">{variantReason}</span> : null}
      </Td>
      <Td>
        {cellInput(variant('price'), variantChanged('price'), cellValid('price', variant('price')), (value) => editVariant(row, 'price', value), {
          label: `${t('gridVariantPrice')} — ${row.sku}`,
          numeric: true,
          // Empty inherits: the base price shows through as the placeholder.
          placeholder: halalasToRiyals(row.basePrice),
        })}
      </Td>
      <Td>
        {cellInput(variant('costPrice'), variantChanged('costPrice'), cellValid('costPrice', variant('costPrice')), (value) => editVariant(row, 'costPrice', value), {
          label: `${t('gridCostPrice')} — ${row.sku}`,
          numeric: true,
          width: 'w-24',
        })}
      </Td>
      <Td>
        {row.stockCounted && mayRecount
          ? cellInput(variant('stock'), variantChanged('stock'), cellValid('stock', variant('stock')), (value) => editVariant(row, 'stock', value), {
              label: `${t('stock')} — ${row.sku}`,
              numeric: true,
              width: 'w-20',
            })
          : <bdi data-numeric className="text-sm text-ink-muted">{row.stockCounted ? count(row.stock, locale) : '—'}</bdi>}
      </Td>
      <Td>
        {cellInput(variant('barcode'), variantChanged('barcode'), true, (value) => editVariant(row, 'barcode', value), {
          label: `${t('barcode')} — ${row.sku}`,
          numeric: true,
          width: 'w-36',
        })}
      </Td>
      <Td>
        {cellInput(variant('mpn'), variantChanged('mpn'), true, (value) => editVariant(row, 'mpn', value), {
          label: `${t('gridMpn')} — ${row.sku}`,
          numeric: true,
          width: 'w-28',
        })}
      </Td>
      <Td>
        {cellInput(variant('weightGrams'), variantChanged('weightGrams'), cellValid('weightGrams', variant('weightGrams')), (value) => editVariant(row, 'weightGrams', value), {
          label: `${t('gridWeight')} — ${row.sku}`,
          numeric: true,
          width: 'w-20',
        })}
      </Td>
    </tr>
  );
}
